import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiJson } from "../utils/api";
import useSeo from "../hooks/useSeo";
import AiModuleTabs from "../components/AiModuleTabs";

const RADAR_KEYS = ["理性", "责任", "表达", "浪漫", "开放"];

function RadarChart({ radar }) {
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const maxR = 78;
  const levels = [0.25, 0.5, 0.75, 1];

  const points = useMemo(() => {
    return RADAR_KEYS.map((key, i) => {
      const angle = (-Math.PI / 2) + (i * 2 * Math.PI) / RADAR_KEYS.length;
      const v = Math.max(0, Math.min(100, Number(radar?.[key]) || 0)) / 100;
      return {
        key,
        value: Math.round(Number(radar?.[key]) || 0),
        x: cx + Math.cos(angle) * maxR * v,
        y: cy + Math.sin(angle) * maxR * v,
        lx: cx + Math.cos(angle) * (maxR + 22),
        ly: cy + Math.sin(angle) * (maxR + 22),
        ax: cx + Math.cos(angle) * maxR,
        ay: cy + Math.sin(angle) * maxR,
      };
    });
  }, [radar, cx, cy, maxR]);

  const poly = points.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
        {levels.map((lv) => (
          <polygon
            key={lv}
            points={RADAR_KEYS.map((_, i) => {
              const angle = (-Math.PI / 2) + (i * 2 * Math.PI) / RADAR_KEYS.length;
              const x = cx + Math.cos(angle) * maxR * lv;
              const y = cy + Math.sin(angle) * maxR * lv;
              return `${x},${y}`;
            }).join(" ")}
            fill="none"
            stroke="#fce7f3"
            strokeWidth="1"
          />
        ))}
        {points.map((p) => (
          <line
            key={`axis-${p.key}`}
            x1={cx}
            y1={cy}
            x2={p.ax}
            y2={p.ay}
            stroke="#fbcfe8"
            strokeWidth="1"
          />
        ))}
        <polygon
          points={poly}
          fill="rgba(244, 63, 94, 0.22)"
          stroke="#f43f5e"
          strokeWidth="2"
        />
        {points.map((p) => (
          <g key={`label-${p.key}`}>
            <circle cx={p.x} cy={p.y} r="3.5" fill="#e11d48" />
            <text
              x={p.lx}
              y={p.ly}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-gray-600"
              style={{ fontSize: 11 }}
            >
              {p.key} {p.value}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function DimCard({ label, value }) {
  return (
    <div className="rounded-xl bg-white border border-rose-100 p-4">
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      <div className="text-sm font-semibold text-gray-800 leading-relaxed">
        {value || "—"}
      </div>
    </div>
  );
}

export default function AiPersonalityPage() {
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const [model, setModel] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState("");

  useSeo({
    title: "我的AI人格 | AI婚恋智能体",
    metaDescription: "查看你的 AI 人格档案、雷达图与成长方向。",
    path: "/ai/personality",
    noindex: true,
  });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent("/ai/personality")}`, {
        replace: true,
      });
    }
  }, [userId, navigate]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const { res, data } = await apiJson(`/api/ai/personality/${userId}`);
      if (res.status === 404) {
        setModel(null);
        setProfile(null);
        setError(data?.detail || "尚未生成人格档案");
        return;
      }
      if (!res.ok) throw new Error(data?.detail || "加载失败");
      setModel(data.model || null);
      setProfile(data.profile || null);
    } catch (err) {
      setError(err.message || "加载失败");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRebuild = async () => {
    if (!userId || rebuilding) return;
    setRebuilding(true);
    try {
      const { res, data } = await apiJson("/api/ai/personality/rebuild", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, force: true }),
      });
      if (!res.ok) throw new Error(data?.detail || "重建失败");
      setProfile(data.profile);
      setModel({
        personality_type: data.profile?.personality_type,
        communication_style: data.profile?.communication_style,
        emotion_style: data.profile?.emotion_style,
        relationship_style: data.profile?.relationship_style,
        value_system: data.profile?.value_system,
        growth_direction: data.profile?.growth_direction,
        radar: data.profile?.radar,
        strengths: data.profile?.strengths,
        risks: data.profile?.risks,
        suggestions: data.profile?.suggestions,
        model_version: data.profile?.model_version,
        source: data.profile?.source,
        profile_score: data.profile?.profile_score,
      });
      setError("");
    } catch (err) {
      alert(err.message || "重建失败");
    } finally {
      setRebuilding(false);
    }
  };

  const reportSummary =
    profile?.ai_report?.summary_text ||
    (typeof profile?.ai_report === "string" ? profile.ai_report : "");

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-pink-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <AiModuleTabs groupKey="persona" />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-rose-400 mb-1">
              AI Personality Model
            </p>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">
              我的 AI 人格
            </h1>
            <p className="text-sm text-gray-500 mt-2 max-w-xl">
              先认识自己，再找到适合的人。人格档案来自深度访谈与长期记忆，可驱动数字分身与匹配建议。
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/ai/twin"
              className="px-4 py-2 rounded-full bg-rose-500 text-white text-sm hover:bg-rose-600"
            >
              和另一个自己对话
            </Link>
            <Link
              to="/ai/consultant"
              className="px-4 py-2 rounded-full border border-rose-300 text-rose-600 text-sm"
            >
              回 AI 咨询师
            </Link>
            <Link
              to="/ai/relationship-map"
              className="px-4 py-2 rounded-full border border-violet-300 text-violet-700 text-sm"
            >
              AI 关系地图
            </Link>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-gray-400 py-16 text-sm">加载中…</p>
        ) : !model ? (
          <div className="rounded-2xl border border-dashed border-rose-200 bg-white p-10 text-center space-y-4">
            <h2 className="font-bold text-gray-800">尚未建立人格档案</h2>
            <p className="text-sm text-gray-500">{error || "请先完成 AI 咨询师访谈。"}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/ai/consultant"
                className="px-5 py-2.5 rounded-full bg-rose-500 text-white text-sm"
              >
                去完成访谈
              </Link>
              <button
                type="button"
                onClick={handleRebuild}
                disabled={rebuilding}
                className="px-5 py-2.5 rounded-full border border-rose-300 text-rose-600 text-sm disabled:opacity-50"
              >
                {rebuilding ? "生成中…" : "基于现有资料快速生成"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <section className="grid md:grid-cols-2 gap-5">
              <div className="rounded-2xl bg-white border border-rose-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold text-gray-900">人格雷达</h2>
                  <span className="text-xs text-gray-400">
                    {model.model_version || "v2"} · {model.source || "—"}
                  </span>
                </div>
                <RadarChart radar={model.radar} />
              </div>
              <div className="rounded-2xl bg-white border border-rose-100 shadow-sm p-6 space-y-4">
                <h2 className="font-bold text-gray-900">核心画像</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <DimCard label="性格" value={model.personality_type} />
                  <DimCard label="沟通" value={model.communication_style} />
                  <DimCard label="情绪" value={model.emotion_style} />
                  <DimCard label="关系模式" value={model.relationship_style} />
                  <DimCard label="核心价值" value={model.value_system} />
                  <DimCard label="成长方向" value={model.growth_direction} />
                </div>
                {typeof model.profile_score === "number" && (
                  <p className="text-xs text-gray-400">
                    档案完整度参考分：{model.profile_score}
                  </p>
                )}
              </div>
            </section>

            <section className="grid md:grid-cols-3 gap-4">
              <div className="rounded-2xl bg-emerald-50 p-5">
                <h3 className="text-sm font-semibold text-emerald-700 mb-2">优势</h3>
                <ul className="space-y-1.5 text-xs text-emerald-900">
                  {(model.strengths || []).map((item) => (
                    <li key={item}>✓ {item}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-amber-50 p-5">
                <h3 className="text-sm font-semibold text-amber-700 mb-2">潜在风险</h3>
                <ul className="space-y-1.5 text-xs text-amber-900">
                  {(model.risks || []).map((item) => (
                    <li key={item}>! {item}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-sky-50 p-5">
                <h3 className="text-sm font-semibold text-sky-700 mb-2">建议</h3>
                <ul className="space-y-1.5 text-xs text-sky-900">
                  {(model.suggestions || []).map((item) => (
                    <li key={item}>→ {item}</li>
                  ))}
                </ul>
              </div>
            </section>

            {reportSummary && (
              <section className="rounded-2xl bg-white border border-gray-100 p-6 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h2 className="font-bold text-gray-900">个人婚恋画像报告（摘要）</h2>
                  <Link to="/ai/consultant" className="text-sm text-rose-600 underline">
                    在咨询师页查看完整报告
                  </Link>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{reportSummary}</p>
              </section>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleRebuild}
                disabled={rebuilding}
                className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-600 hover:border-rose-300 disabled:opacity-50"
              >
                {rebuilding ? "重建中…" : "重新计算人格模型"}
              </button>
              <Link
                to="/center/ai-memory"
                className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-600"
              >
                管理长期记忆
              </Link>
              <Link
                to="/ai/match"
                className="px-4 py-2 rounded-full border border-rose-300 text-rose-600 text-sm"
              >
                去 AI 匹配
              </Link>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
